/**
 * Cancellation Utilities
 * 
 * Event-based cancellation helpers to avoid polluting tool code with signal checks
 */

/**
 * Error thrown when operation is cancelled
 */
export class CancellationError extends Error {
  constructor(message: string = 'Operation cancelled by user') {
    super(message);
    this.name = 'CancellationError';
  }
}

/**
 * Wraps an async operation to make it cancellable via AbortSignal events.
 * When the signal is aborted, the operation is immediately rejected with CancellationError.
 * 
 * @example
 * ```typescript
 * const result = await withCancellation(
 *   fetchAndProcessData(),
 *   context.signal
 * );
 * ```
 */
export async function withCancellation<T>(
  promise: Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  // If no signal, just return the promise as-is
  if (!signal) {
    return promise;
  }

  // If already aborted, reject immediately
  if (signal.aborted) {
    throw new CancellationError();
  }

  // Race the promise against cancellation
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      signal.addEventListener('abort', () => {
        reject(new CancellationError());
      }, { once: true });
    })
  ]);
}

/**
 * Creates a cancellable context that can be checked or awaited.
 * Useful for long-running operations that need to periodically check cancellation.
 * 
 * @example
 * ```typescript
 * const cancellable = createCancellable(context.signal);
 * 
 * for (const item of items) {
 *   await cancellable.checkPoint(); // Throws if cancelled
 *   await processItem(item);
 * }
 * ```
 */
export function createCancellable(signal?: AbortSignal) {
  let cancelled = false;
  
  if (signal) {
    cancelled = signal.aborted;
    signal.addEventListener('abort', () => {
      cancelled = true;
    }, { once: true });
  }

  return {
    /**
     * Check if cancelled (synchronous)
     */
    get isCancelled() {
      return cancelled;
    },

    /**
     * Throw CancellationError if cancelled (synchronous checkpoint)
     */
    checkPoint() {
      if (cancelled) {
        throw new CancellationError();
      }
    },

    /**
     * Async checkpoint - useful for breaking async loops
     */
    async asyncCheckPoint() {
      if (cancelled) {
        throw new CancellationError();
      }
    },

    /**
     * Wrap a promise to make it cancellable
     */
    wrap<T>(promise: Promise<T>): Promise<T> {
      return withCancellation(promise, signal);
    }
  };
}

/**
 * Wraps an async generator to make it cancellable.
 * Each iteration checks for cancellation.
 * 
 * @example
 * ```typescript
 * for await (const item of cancellableIterator(processItems(), context.signal)) {
 *   // Process item
 * }
 * ```
 */
export async function* cancellableIterator<T>(
  iterator: AsyncIterable<T>,
  signal?: AbortSignal
): AsyncGenerator<T> {
  if (!signal) {
    yield* iterator;
    return;
  }

  const cancellable = createCancellable(signal);

  for await (const item of iterator) {
    cancellable.checkPoint(); // Throw if cancelled
    yield item;
  }
}

